"use client";

import Image from "next/image";
import { Dialog } from "@base-ui/react/dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import type { GalleryItem } from "@/content/gallery";

interface GalleryLightboxProps {
  items: GalleryItem[];
  /** Indice dell'immagine aperta, null = chiuso. */
  index: number | null;
  onIndexChange: (index: number | null) => void;
}

const controlClass =
  "flex size-11 items-center justify-center rounded-full border border-avorio/25 bg-inchiostro/60 text-avorio transition-colors hover:bg-avorio hover:text-inchiostro focus-visible:ring-2 focus-visible:ring-avorio/70 focus-visible:outline-none";

/** Foto a tutto schermo con frecce (anche da tastiera ← →) e chiusura con Esc o click fuori. */
export function GalleryLightbox({ items, index, onIndexChange }: GalleryLightboxProps) {
  const t = useTranslations("galleria.lightbox");
  const locale = useLocale();
  const item = index === null ? null : items[index];

  const go = (delta: number) => {
    if (index === null) return;
    onIndexChange((index + delta + items.length) % items.length);
  };

  return (
    <Dialog.Root open={index !== null} onOpenChange={(open) => !open && onIndexChange(null)}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-50 bg-inchiostro/95" />
        <Dialog.Popup
          className="fixed inset-0 z-50 flex flex-col text-avorio outline-none"
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") go(-1);
            if (event.key === "ArrowRight") go(1);
          }}
        >
          <Dialog.Title className="sr-only">{item ? (locale === "en" ? item.en : item.it) : t("label")}</Dialog.Title>

          <div
            className="relative min-h-0 flex-1 p-3 md:px-24 md:pt-8 md:pb-4"
            onClick={(event) => event.target === event.currentTarget && onIndexChange(null)}
          >
            {item ? (
              <Image
                key={item.id}
                src={item.src}
                alt={locale === "en" ? item.en : item.it}
                fill
                sizes="100vw"
                className="pointer-events-none object-contain p-3 md:px-24 md:pt-8 md:pb-4"
              />
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-4 px-5 pt-2 pb-5 md:px-10 md:pb-8">
            <div className="min-w-0">
              <p className="truncate font-display text-lg italic md:text-xl">
                {item ? (locale === "en" ? item.en : item.it) : ""}
              </p>
              <p className="eyebrow mt-1 text-avorio/60!">
                {index === null ? "" : t("counter", { current: index + 1, total: items.length })}
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button type="button" className={controlClass} aria-label={t("prev")} onClick={() => go(-1)}>
                <ChevronLeft className="size-5" aria-hidden="true" />
              </button>
              <button type="button" className={controlClass} aria-label={t("next")} onClick={() => go(1)}>
                <ChevronRight className="size-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          <Dialog.Close className={`${controlClass} absolute top-4 right-4 md:top-6 md:right-8`} aria-label={t("close")}>
            <X className="size-5" aria-hidden="true" />
          </Dialog.Close>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
